import { styled } from "goober";
import type { ReactNode } from "react";
import { Icon } from "../../Icon";
import { metaJoin } from "../../MetaSeparator";
import { PressButton } from "../../PressButton";
import { Text } from "../../Text";
import { Tooltip } from "../../Tooltip";
import type { EdgeType, GraphNode } from "./buildAccessModel";
import { edgeVisual } from "./edgeColor";

const Footer = styled("div")`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  min-height: 36px;
  padding: 6px 24px;
  background: var(--surface-secondary);
  box-shadow: 0 -0.5px 0 var(--border-alpha);
`;

const Left = styled("div")`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;

const Right = styled("div")`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
`;

const Visual = styled("div")`
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

// A toggle badge: its glyph (icon or line sample) and label inherit the badge's
// color, so on = alpha4 + text-primary and off = transparent + text-secondary.
const Badge = styled(PressButton)`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  height: 20px;
  padding: 0 8px 0 6px;
  border-radius: 38px;
  background: var(--alpha4);
  color: var(--text-primary);
  transition: background 120ms ease;

  &:hover {
    background: var(--alpha8);
  }
  /* The chip is always alpha4; the on/off state shows through the glyph + label
     opacity instead, so the background stays constant. */
  & > * {
    opacity: 0.4;
    transition: opacity 120ms ease;
  }
  &[data-on="true"] > * {
    opacity: 1;
  }
`;

// Reuses each edge type's dash array so the sample matches the graph exactly;
// stroke is currentColor so it follows the badge's on/off color.
function LineGlyph({ type }: { type: EdgeType }) {
  const { dash } = edgeVisual(type, "rest");
  return (
    <svg width="16" height="2" style={{ overflow: "visible", flexShrink: 0 }}>
      <line
        x1="0"
        y1="1"
        x2="16"
        y2="1"
        stroke="currentColor"
        strokeWidth="1"
        strokeDasharray={dash}
        strokeLinecap="round"
      />
    </svg>
  );
}

function LayerBadge({
  on,
  glyph,
  label,
  tooltip,
  layer,
  onToggle,
  onHoverLayer,
}: {
  on: boolean;
  glyph: ReactNode;
  label: string;
  tooltip: string;
  layer?: EdgeType;
  onToggle: () => void;
  onHoverLayer?: (layer: EdgeType | null) => void;
}) {
  return (
    <Tooltip content={tooltip} contentStyle={{ maxWidth: 200 }}>
      <Badge
        data-no-pill
        data-on={on}
        onMouseEnter={
          layer && onHoverLayer ? () => onHoverLayer(layer) : undefined
        }
        onMouseLeave={
          layer && onHoverLayer ? () => onHoverLayer(null) : undefined
        }
        onMouseDown={(e) => e.preventDefault()}
        onPress={onToggle}
      >
        {glyph}
        <Text variant="text-small-md" color="inherit">
          {label}
        </Text>
      </Badge>
    </Tooltip>
  );
}

type Props = {
  node: GraphNode | null;
  visual: ReactNode;
  summary: string[];
  hideUnrelated: boolean;
  onToggleHide: () => void;
  showDirect: boolean;
  onToggleDirect: () => void;
  showInherited: boolean;
  onToggleInherited: () => void;
  showAvailable: boolean;
  onToggleAvailable: () => void;
  onHoverLayer: (layer: EdgeType | null) => void;
};

// Fixed footer: interaction guidance until a card is selected, then the focused
// node's identity + effective-access summary. The layer badges stay on the right.
export function BottomToolbar({
  node,
  visual,
  summary,
  hideUnrelated,
  onToggleHide,
  showDirect,
  onToggleDirect,
  showInherited,
  onToggleInherited,
  showAvailable,
  onToggleAvailable,
  onHoverLayer,
}: Props) {
  return (
    <Footer>
      <Left>
        {node ? (
          <>
            <Visual>{visual}</Visual>
            <Text variant="text-small-md" color="var(--text-primary)" ellipsis>
              {node.name}
            </Text>
            {summary.length > 0 && (
              <Text variant="text-small" color="var(--text-secondary)">
                {metaJoin(summary)}
              </Text>
            )}
          </>
        ) : (
          <Text variant="text-small" color="var(--text-secondary)">
            Click to focus&nbsp;&nbsp;•&nbsp;&nbsp;Drag to grant
            access&nbsp;&nbsp;•&nbsp;&nbsp;Click ⋮ for actions
          </Text>
        )}
      </Left>
      <Right>
        <LayerBadge
          on={hideUnrelated}
          glyph={<Icon name="layers" size={10} />}
          label="Collapse"
          tooltip="Hide everything but the selected card and its links, closing the gaps."
          onToggle={onToggleHide}
        />
        <LayerBadge
          on={showDirect}
          glyph={<LineGlyph type="direct" />}
          label="Direct"
          tooltip="Group memberships and access granted straight to a person or group."
          layer="direct"
          onToggle={onToggleDirect}
          onHoverLayer={onHoverLayer}
        />
        <LayerBadge
          on={showInherited}
          glyph={<LineGlyph type="inherited" />}
          label="Inherited"
          tooltip="Access a person gets through a group they belong to, shown for the selected card."
          layer="inherited"
          onToggle={onToggleInherited}
          onHoverLayer={onHoverLayer}
        />
        <LayerBadge
          on={showAvailable}
          glyph={<LineGlyph type="derived" />}
          label="Available"
          tooltip="Connectors and skills a space can use through its members."
          layer="derived"
          onToggle={onToggleAvailable}
          onHoverLayer={onHoverLayer}
        />
      </Right>
    </Footer>
  );
}
